import { DEFAULT_TF, tfOf } from './config'
import type { Candle, TfKey } from './types'

export type PricePoint = { t: number; price: number; volume: number }

export const bucketStart = (t: number, step: number) => t - (t % step)

const valid = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0

/**
 * OHLC candles from swap prices. Each candle opens at the previous candle's close so the chart is continuous;
 * the first opens at its own first price. Empty buckets up to `until` repeat the last close with zero volume, so a quiet pool
 * draws a flat line instead of a hole. At most `limit` newest candles are kept; buckets older than the window still set the open.
 */
export function buildCandles(points: PricePoint[], step: number, opts: { until?: number; limit?: number } = {}): Candle[] {
  const pts = points.filter((p) => valid(p.price) && Number.isFinite(p.t) && p.t > 0).sort((a, b) => a.t - b.t)
  if (!pts.length) return []
  const limit = opts.limit ?? 300
  const byBucket = new Map<number, { o: number; h: number; l: number; c: number; v: number }>()
  pts.forEach((p) => {
    const b = bucketStart(p.t, step)
    const cur = byBucket.get(b)
    const vol = Number.isFinite(p.volume) && p.volume > 0 ? p.volume : 0
    if (!cur) byBucket.set(b, { o: p.price, h: p.price, l: p.price, c: p.price, v: vol })
    else {
      cur.h = Math.max(cur.h, p.price)
      cur.l = Math.min(cur.l, p.price)
      cur.c = p.price
      cur.v += vol
    }
  })
  const first = bucketStart(pts[0].t, step)
  const last = bucketStart(Math.max(opts.until ?? 0, pts[pts.length - 1].t), step)
  const start = Math.max(first, last - (limit - 1) * step)
  // the close before the window opens the first visible candle
  let prev: number | null = null
  byBucket.forEach((c, b) => {
    if (b < start && (prev === null || b > (prev as number))) prev = b
  })
  let prevClose: number | null = prev === null ? null : (byBucket.get(prev) as { c: number }).c
  const out: Candle[] = []
  for (let b = start; b <= last; b += step) {
    const c = byBucket.get(b)
    if (c) {
      const o = prevClose ?? c.o
      out.push({ t: b, o, h: Math.max(c.h, o), l: Math.min(c.l, o), c: c.c, v: c.v })
      prevClose = c.c
    } else if (prevClose !== null) {
      out.push({ t: b, o: prevClose, h: prevClose, l: prevClose, c: prevClose, v: 0 })
    }
  }
  return out
}

/** GeckoTerminal OHLCV answer: data.attributes.ohlcv_list = [[unix, o, h, l, c, volume], ...] newest first. */
export function parseGeckoOhlcv(json: any): Candle[] {
  const list = json?.data?.attributes?.ohlcv_list
  if (!Array.isArray(list)) return []
  const out: Candle[] = []
  list.forEach((row) => {
    if (!Array.isArray(row) || row.length < 6) return
    const [t, o, h, l, c, v] = row.map(Number)
    if (![t, o, h, l, c].every(Number.isFinite) || t <= 0 || ![o, h, l, c].every((x) => x > 0)) return
    out.push({ t, o, h: Math.max(h, o, c), l: Math.min(l, o, c), c, v: Number.isFinite(v) && v > 0 ? v : 0 })
  })
  return out.sort((a, b) => a.t - b.t).filter((c, i, arr) => i === 0 || c.t !== arr[i - 1].t)
}

/** Path of the OHLCV request; `token` (an address) asks GeckoTerminal to chart that token's USD price whichever side it is. */
export function geckoOhlcvPath(pool: string, tf: TfKey, token: string): string {
  const cfg = tfOf(tf)
  return `/networks/bsc/pools/${pool}/ohlcv/${cfg.unit}?aggregate=${cfg.aggregate}&limit=${cfg.limit}&currency=usd&token=${token}`
}

/** Seconds of history a timeframe's swap query must reach back to fill its chart (subgraph source). */
export const historyWindow = (tf: TfKey): number => tfOf(tf).seconds * tfOf(tf).limit

/**
 * The timeframe a pool's chart opens on. A pool that traded inside the default window keeps the default; a dormant one (every CADINUdex pool today)
 * opens on the finest frame whose history still reaches its last trade, so the first view is a chart and not an empty state.
 * The user's own choice always wins over this (the page only calls it while nothing was picked).
 */
export function timeframeFor(lastTradeAt: number | null | undefined, nowSec: number): TfKey {
  if (!lastTradeAt || lastTradeAt > nowSec) return DEFAULT_TF
  const age = nowSec - lastTradeAt
  const reaches: TfKey[] = ['15m', '1h', '4h', '1d']
  // 10 % of each window is kept as a margin so the last trade is never on the very edge of the candles
  return reaches.find((k) => age < historyWindow(k) * 0.9) ?? '1d'
}
